/**
 * OutlierFilter: Statistical outlier detection and robust aggregation for bike fit measurements.
 * 
 * Uses IQR (Interquartile Range) method for outlier detection and median for robust central tendency.
 */

/**
 * Calculate median of an array of numbers.
 * @param {number[]} values - Array of numbers
 * @returns {number|null} - Median value, or null if array is empty
 */
export function median(values) {
  if (!values || values.length === 0) return null;
  
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  } else {
    return sorted[mid];
  }
}

/**
 * Calculate percentile of an array of numbers.
 * @param {number[]} values - Array of numbers
 * @param {number} p - Percentile (0-100)
 * @returns {number|null} - Percentile value, or null if array is empty
 */
export function percentile(values, p) {
  if (!values || values.length === 0) return null;
  
  const sorted = [...values].sort((a, b) => a - b);
  const index = (p / 100) * (sorted.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;
  
  if (lower === upper) {
    return sorted[lower];
  }
  
  return sorted[lower] * (1 - weight) + sorted[upper] * weight;
}

/**
 * Calculate IQR (Interquartile Range) and outlier bounds.
 * @param {number[]} values - Array of numbers
 * @returns {Object} - { q1, q3, iqr, lowerBound, upperBound }
 */
export function calculateIQR(values) {
  if (!values || values.length < 3) {
    return null;
  }
  
  const q1 = percentile(values, 25);
  const q3 = percentile(values, 75);
  const iqr = q3 - q1;
  
  // Standard IQR method: 1.5 * IQR
  const lowerBound = q1 - 1.5 * iqr;
  const upperBound = q3 + 1.5 * iqr;
  
  return {
    q1,
    q3,
    iqr,
    lowerBound,
    upperBound,
  };
}

/**
 * Remove outliers from an array using IQR method.
 * @param {number[]} values - Array of numbers
 * @param {number} multiplier - IQR multiplier for bounds (default: 1.5)
 * @returns {Object} - { filtered: number[], removed: number[], stats: Object }
 */
export function removeOutliersIQR(values, multiplier = 1.5) {
  if (!values || values.length === 0) {
    return {
      filtered: [],
      removed: [],
      stats: null,
    };
  }
  
  // Need at least 3 values for meaningful IQR
  if (values.length < 3) {
    return {
      filtered: [...values],
      removed: [],
      stats: null,
    };
  }
  
  const iqrStats = calculateIQR(values);
  
  // Adjust bounds with custom multiplier
  const lowerBound = iqrStats.q1 - multiplier * iqrStats.iqr;
  const upperBound = iqrStats.q3 + multiplier * iqrStats.iqr;
  
  const filtered = [];
  const removed = [];
  
  for (const value of values) {
    if (value >= lowerBound && value <= upperBound) {
      filtered.push(value);
    } else {
      removed.push(value);
    }
  }
  
  return {
    filtered,
    removed,
    stats: {
      ...iqrStats,
      lowerBound,
      upperBound,
      originalCount: values.length,
      filteredCount: filtered.length,
      removedCount: removed.length,
      removalRate: removed.length / values.length,
    },
  };
}

/**
 * Filter outliers with temporal consistency check.
 * Removes values that differ significantly from previous measurement.
 * @param {Array} measurements - Array of { value, timestamp, frameIndex }
 * @param {number} maxDelta - Maximum allowed change between consecutive measurements (degrees)
 * @returns {Object} - { filtered, removed, stats }
 */
export function filterWithTemporalConsistency(measurements, maxDelta = 10) {
  if (!measurements || measurements.length < 2) {
    return {
      filtered: measurements || [],
      removed: [],
      stats: null,
    };
  }
  
  const filtered = [measurements[0]]; // Always keep first measurement
  const removed = [];
  
  for (let i = 1; i < measurements.length; i++) {
    const current = measurements[i];
    const previous = filtered[filtered.length - 1];
    
    const delta = Math.abs(current.value - previous.value);
    
    if (delta <= maxDelta) {
      filtered.push(current);
    } else {
      removed.push(current);
    }
  }
  
  return {
    filtered,
    removed,
    stats: {
      originalCount: measurements.length,
      filteredCount: filtered.length,
      removedCount: removed.length,
      removalRate: removed.length / measurements.length,
      maxDelta,
    },
  };
}

/**
 * Calculate robust statistics for angle measurements.
 * @param {number[]} values - Array of angle values
 * @returns {Object} - Robust statistics
 */
export function calculateRobustStats(values) {
  if (!values || values.length === 0) {
    return null;
  }
  
  // Remove outliers first
  const { filtered, removed, stats: iqrStats } = removeOutliersIQR(values);
  
  if (filtered.length === 0) {
    return {
      median: null,
      mean: null,
      min: null,
      max: null,
      range: null,
      std: null,
      count: 0,
      outliersRemoved: removed.length,
      iqrStats,
    };
  }
  
  const medianVal = median(filtered);
  const meanVal = filtered.reduce((a, b) => a + b, 0) / filtered.length;
  const minVal = Math.min(...filtered);
  const maxVal = Math.max(...filtered);
  const range = maxVal - minVal;
  
  // Calculate standard deviation
  const variance = filtered.reduce((sum, v) => sum + (v - meanVal) ** 2, 0) / filtered.length;
  const std = Math.sqrt(variance);
  
  return {
    median: Math.round(medianVal * 10) / 10,
    mean: Math.round(meanVal * 10) / 10,
    min: Math.round(minVal * 10) / 10,
    max: Math.round(maxVal * 10) / 10,
    range: Math.round(range * 10) / 10,
    std: Math.round(std * 10) / 10,
    count: filtered.length,
    outliersRemoved: removed.length,
    iqrStats,
  };
}

/**
 * Extract angle values at specific pedal position from labeled frames.
 * @param {Array} labeledFrames - Frames with pedalPosition labels
 * @param {string} position - 'BDC' or 'TDC'
 * @param {string} angleKey - Angle key (e.g., 'knee', 'hip')
 * @returns {number[]} - Array of angle values at that position
 */
export function extractAnglesAtPosition(labeledFrames, position, angleKey) {
  return labeledFrames
    .filter(f => f.pedalPosition === position)
    .map(f => f.angles[angleKey])
    .filter(v => v !== undefined && !isNaN(v));
}

/**
 * Get robust measurement at specific pedal position with outlier filtering.
 * Now more selective - only uses frames closest to exact peak/trough position.
 * @param {Array} labeledFrames - Frames with pedalPosition labels
 * @param {string} position - 'BDC' or 'TDC'
 * @param {string} angleKey - Angle key (e.g., 'knee', 'hip')
 * @param {number} maxFramesPerCycle - Maximum frames to use per cycle (default: 1 = only exact position)
 * @returns {Object|null} - { value, count, outliersRemoved, stats } or null
 */
export function getRobustMeasurementAtPosition(labeledFrames, position, angleKey, maxFramesPerCycle = 1) {
  // Group frames by cycle ID
  const framesByCycle = {};
  
  labeledFrames.forEach(frame => {
    if (frame.pedalPosition === position && frame.cycleId !== null) {
      if (!framesByCycle[frame.cycleId]) {
        framesByCycle[frame.cycleId] = [];
      }
      framesByCycle[frame.cycleId].push(frame);
    }
  });
  
  // For each cycle, take only the best frame(s)
  const selectedValues = [];
  
  Object.values(framesByCycle).forEach(cycleFrames => {
    // Sort by how close they are to the exact peak/trough
    // (frameIndex should be closest to the detected peak/trough)
    // For now, just take the first N frames
    const framesToUse = cycleFrames.slice(0, maxFramesPerCycle);
    
    framesToUse.forEach(frame => {
      const value = frame.angles[angleKey];
      if (value !== undefined && !isNaN(value)) {
        selectedValues.push(value);
      }
    });
  });
  
  if (selectedValues.length === 0) {
    return null;
  }
  
  const stats = calculateRobustStats(selectedValues);
  
  if (!stats || stats.median === null) {
    return null;
  }
  
  return {
    value: stats.median,
    count: stats.count,
    outliersRemoved: stats.outliersRemoved,
    stats,
    cyclesUsed: Object.keys(framesByCycle).length,
    framesPerCycle: maxFramesPerCycle,
  };
}
